import {
  Box,
  Button,
  Drawer,
  Field,
  Flex,
  HStack,
  Heading,
  Input,
  Portal,
  Select,
  SimpleGrid,
  Text,
  createListCollection,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { LuInfo, LuSave, LuTrash2 } from 'react-icons/lu'
import type { ContactBookDto } from '@/api/dto/settings.dto'
import { DeleteAdvisorDialog } from '@/components/modals/DeleteAdvisorDialog'
import { Tooltip } from '@/components/ui/tooltip'

function padAdvisorNumber(value: string) {
  return value.padStart(8, '0')
}

export interface BankAdvisorDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  initialAdvisor?: ContactBookDto | null
  customerOptions: Array<{ label: string; value: string }>
  onSubmit: (values: {
    customerId: number
    firstName: string
    lastName: string
    advisorNumber: string
  }) => Promise<void>
  onDelete?: (id: number) => Promise<void>
  isSubmitting?: boolean
}

export function BankAdvisorDrawer({
  open,
  onOpenChange,
  mode,
  initialAdvisor,
  customerOptions,
  onSubmit,
  onDelete,
  isSubmitting = false,
}: BankAdvisorDrawerProps) {
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [advisorNumber, setAdvisorNumber] = useState('')

  useEffect(() => {
    if (!open) return

    setFirstName(initialAdvisor?.firstName ?? '')
    setLastName(initialAdvisor?.lastName ?? '')
    setCustomerId(initialAdvisor?.customerId ? String(initialAdvisor.customerId) : '')
    setAdvisorNumber(initialAdvisor?.advisorNumber ?? '')
  }, [initialAdvisor, open])

  const customerCollection = useMemo(
    () => createListCollection({ items: customerOptions }),
    [customerOptions]
  )

  const advisorNumberIsValid = /^\d{1,8}$/.test(advisorNumber)
  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    customerId.length > 0 &&
    advisorNumberIsValid

  async function handleSubmit() {
    if (!canSubmit) return

    await onSubmit({
      customerId: Number(customerId),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      advisorNumber: padAdvisorNumber(advisorNumber.trim()),
    })
  }

  async function handleDelete() {
    if (!initialAdvisor?.id || !onDelete) return
    await onDelete(initialAdvisor.id)
    setDeleteOpen(false)
  }

  return (
    <Drawer.Root open={open} onOpenChange={details => onOpenChange(details.open)} size="xl">
      <Portal>
        <Drawer.Backdrop />

        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header>
              <HStack gap={4}>
                <Drawer.Title>Beraterdetails eintragen</Drawer.Title>

                {mode === 'edit' && initialAdvisor?.id && (
                  <Button
                    variant="subtle"
                    colorPalette="red"
                    onClick={() => setDeleteOpen(true)}
                    disabled={isSubmitting}
                  >
                    <LuTrash2 />
                    Berater löschen
                  </Button>
                )}
              </HStack>
            </Drawer.Header>

            <Drawer.Body>
              <Flex direction="column" gap={8}>
                <Flex direction="column" gap={4}>
                  <Heading size="xl">Stammdaten</Heading>

                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                    <Field.Root required>
                      <Field.Label>
                        Vorname <Field.RequiredIndicator />
                      </Field.Label>
                      <Input
                        placeholder="Max"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                      />
                    </Field.Root>

                    <Field.Root required>
                      <Field.Label>
                        Nachname <Field.RequiredIndicator />
                      </Field.Label>
                      <Input
                        placeholder="Muster"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                      />
                    </Field.Root>

                    <Field.Root required>
                      <Field.Label>
                        Leistungsempfänger <Field.RequiredIndicator />
                        <Tooltip
                          positioning={{ strategy: 'fixed', placement: 'top' }}
                          showArrow
                          content="Der Berater wird beim Erstellen einer Aktivität nur für den ausgewählten Leistungsempfänger vorgeschlagen. Angezeigt werden nur Leistungsempfänger aus freigegebenen Bestellungen."
                        >
                          <Box color="fg.muted" cursor="help">
                            <LuInfo />
                          </Box>
                        </Tooltip>
                      </Field.Label>
                      <Select.Root
                        collection={customerCollection}
                        value={customerId ? [customerId] : []}
                        onValueChange={({ value }) => setCustomerId(value[0] ?? '')}
                        positioning={{
                          strategy: 'fixed',
                          placement: 'bottom-start',
                          sameWidth: true,
                          offset: { mainAxis: 6, crossAxis: 0 },
                        }}
                      >
                        <Select.Trigger>
                          <Select.ValueText placeholder="Bitte auswählen" />
                          <Select.Indicator />
                        </Select.Trigger>

                        <Select.Positioner>
                          <Select.Content w="var(--reference-width)">
                            {customerCollection.items.length === 0 ? (
                              <Select.Item item="" disabled>
                                Keine Leistungsempfänger verfügbar
                              </Select.Item>
                            ) : (
                              customerCollection.items.map(item => (
                                <Select.Item key={item.value} item={item}>
                                  {item.label}
                                </Select.Item>
                              ))
                            )}
                          </Select.Content>
                        </Select.Positioner>
                      </Select.Root>
                    </Field.Root>

                    <Field.Root
                      required
                      invalid={advisorNumber.length > 0 && !advisorNumberIsValid}
                    >
                      <Field.Label>
                        Beraternummer <Field.RequiredIndicator />
                      </Field.Label>
                      <Input
                        placeholder="00001234"
                        value={advisorNumber}
                        maxLength={8}
                        onChange={e =>
                          setAdvisorNumber(e.target.value.replace(/\D/g, '').slice(0, 8))
                        }
                      />
                      <Field.ErrorText>
                        Die Beraternummer darf nur aus Ziffern bestehen und maximal 8 Stellen
                        haben.
                      </Field.ErrorText>
                    </Field.Root>
                  </SimpleGrid>
                </Flex>

                <Box>
                  <HStack justify="flex-end">
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>
                      Abbrechen
                    </Button>

                    <Button
                      variant="solid"
                      colorPalette="blue"
                      disabled={!canSubmit || isSubmitting}
                      loading={isSubmitting}
                      onClick={handleSubmit}
                    >
                      <LuSave />
                      Speichern
                    </Button>
                  </HStack>
                </Box>
              </Flex>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
      <DeleteAdvisorDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDelete={handleDelete}
        loading={isSubmitting}
      />
    </Drawer.Root>
  )
}
